import { useRef, useState } from "react";
import { ChevronLeft, ChevronRight, ExternalLink, Play } from "lucide-react";
import { Button, Section, SectionHeading, StickerCard } from "@/components/ui";
import { VIDEO_TESTIMONIALS } from "@/data/videoTestimonials";

/** Loads the YouTube iframe only after a click, so the page stays fast. */
function VideoTestimonialCard({ video, index, isPlaying, onPlay }) {
  const hasVideo = Boolean(video.youtubeId);

  return (
    <StickerCard tone={index % 2 === 0 ? "brandTint" : "sunTint"} wiggle={false} className="flex h-full flex-col gap-3 !p-3 sm:!p-4">
      <div className="relative aspect-[9/16] w-full overflow-hidden rounded-2xl bg-ink/5 sm:aspect-video">
        {isPlaying && hasVideo ? (
          <iframe
            className="absolute inset-0 h-full w-full"
            src={`https://www.youtube-nocookie.com/embed/${video.youtubeId}?autoplay=1&rel=0`}
            title={`${video.name}: ${video.line}`}
            allow="autoplay; encrypted-media; picture-in-picture"
            allowFullScreen
            loading="lazy"
          />
        ) : (
          <button
            type="button"
            disabled={!hasVideo}
            onClick={onPlay}
            aria-label={hasVideo ? `Play video: ${video.line}` : "Video coming soon"}
            className="group absolute inset-0 flex flex-col items-center justify-center gap-2 disabled:cursor-default"
          >
            {hasVideo ? (
              <img
                src={`https://img.youtube.com/vi/${video.youtubeId}/hqdefault.jpg`}
                alt=""
                width={480}
                height={360}
                className="absolute inset-0 h-full w-full object-cover"
                loading="lazy"
              />
            ) : null}
            <span className="relative inline-flex h-14 w-14 items-center justify-center rounded-full bg-brand-500 text-white shadow-soft transition-transform group-enabled:group-hover:scale-110 sm:h-16 sm:w-16">
              <Play className="h-6 w-6 translate-x-0.5 sm:h-7 sm:w-7" aria-hidden="true" />
            </span>
          </button>
        )}
      </div>
      <div className="px-1 pb-1 sm:px-2 sm:pb-2">
        <h4 className="font-heading text-base font-bold leading-snug sm:text-lg">{video.line}</h4>
        <p className="mt-1 text-sm text-ink/70">{video.name}</p>
      </div>
    </StickerCard>
  );
}

export function VideoTestimonialsSection() {
  const trackRef = useRef(null);
  const [activeVideoId, setActiveVideoId] = useState(null);
  const { title, highlight, intro, videos, playlistUrl, playlistLabel } = VIDEO_TESTIMONIALS;

  const scrollByCard = (direction) => {
    const el = trackRef.current;
    if (!el) return;
    const slide = el.querySelector(":scope > li");
    const step = slide ? slide.offsetWidth + 12 : Math.round(el.clientWidth * 0.85);
    el.scrollBy({ left: direction * step, behavior: "smooth" });
  };

  return (
    <Section tone="brandTint" labelledBy="video-testimonials-title" testId="home-video-testimonials">
      <div className="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
        <SectionHeading id="video-testimonials-title" title={title} highlight={highlight} intro={intro} className="sm:max-w-2xl" />
        <div className="flex shrink-0 gap-2 self-start sm:self-auto">
          <button
            type="button"
            onClick={() => scrollByCard(-1)}
            aria-label="Previous testimonial videos"
            className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-brand-500 bg-white text-brand-500 transition-colors hover:bg-brand-500 hover:text-white"
          >
            <ChevronLeft className="h-5 w-5" aria-hidden="true" />
          </button>
          <button
            type="button"
            onClick={() => scrollByCard(1)}
            aria-label="Next testimonial videos"
            className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-brand-500 bg-white text-brand-500 transition-colors hover:bg-brand-500 hover:text-white"
          >
            <ChevronRight className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>
      </div>

      <ul
        ref={trackRef}
        tabIndex={0}
        aria-label="Video testimonials, scroll sideways"
        data-testid="video-testimonials-carousel"
        className="snap-row -mx-4 mt-8 flex snap-x snap-mandatory scroll-px-4 gap-3 overflow-x-auto px-4 pb-3 sm:mx-0 sm:scroll-px-0 sm:px-0 lg:gap-4"
      >
        {videos.map((video, i) => (
          <li
            key={video.id}
            className="w-[82%] shrink-0 snap-start sm:w-[calc((100%-0.75rem)/2)] lg:w-[calc((100%-2rem)/3)]"
          >
            <VideoTestimonialCard
              video={video}
              index={i}
              isPlaying={activeVideoId === video.id}
              onPlay={() => setActiveVideoId(video.id)}
            />
          </li>
        ))}
      </ul>

      <div className="mt-8 flex justify-center sm:justify-start">
        <Button variant="outline" size="lg" href={playlistUrl} external>
          {playlistLabel}
          <ExternalLink className="h-4 w-4" aria-hidden="true" />
        </Button>
      </div>
    </Section>
  );
}
